/**
 * OrderTrackingCard — Händler-Steuerung der EU-Bestellungs-Nachverfolgung.
 * Zeigt den aktuellen Fortschritt (bestellt → in Produktion → im Transit →
 * eingetroffen), erlaubt Status-/ETA-/Notiz-Pflege und eine optionale
 * (gemockte) Kunden-Benachrichtigung. Der Kunden-Link (Token) kann kopiert werden.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Truck, Loader2, Copy, Check, Bell } from "lucide-react";
import api from "../../lib/api";

export const ORDER_STAGES = [
    { key: "bestellt", label: "Bestellt" },
    { key: "in_produktion", label: "In Produktion" },
    { key: "im_transit", label: "Im Transit" },
    { key: "eingetroffen", label: "Eingetroffen" },
];

export default function OrderTrackingCard({ listing, onUpdated }) {
    const tr = listing.order_tracking || {};
    const [status, setStatus] = useState(tr.status || "bestellt");
    const [eta, setEta] = useState(tr.eta || "");
    const [note, setNote] = useState("");
    const [notify, setNotify] = useState(false);
    const [custName, setCustName] = useState(tr.customer_name || "");
    const [custEmail, setCustEmail] = useState(tr.customer_email || "");
    const [saving, setSaving] = useState(false);
    const [token, setToken] = useState(tr.customer_token || "");
    const [copied, setCopied] = useState(false);

    const activeIdx = ORDER_STAGES.findIndex((s) => s.key === status);
    const trackUrl = token ? `${window.location.origin}/bestellung/${token}` : "";

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(`/dealer/listings/${listing.id}/order-tracking`, {
                status, eta, note, notify, customer_name: custName, customer_email: custEmail,
            });
            setToken(data.tracking?.customer_token || token);
            setNote("");
            setNotify(false);
            toast.success(
                notify
                    ? "Status gespeichert · Kunden-Benachrichtigung protokolliert (Versand gemockt)."
                    : "Bestell-Status gespeichert."
            );
            onUpdated?.(data.tracking);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Status konnte nicht gespeichert werden.");
        } finally {
            setSaving(false);
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(trackUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.info(trackUrl);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4 space-y-4" data-testid="order-tracking-card">
            {/* Stepper */}
            <div className="flex items-center gap-1" data-testid="order-tracking-stepper">
                {ORDER_STAGES.map((s, i) => {
                    const done = i <= activeIdx;
                    return (
                        <div key={s.key} className="flex-1 flex items-center gap-1 min-w-0">
                            <div className={`flex-1 h-1.5 rounded-full ${done ? "bg-[#111827]" : "bg-gray-200"}`} />
                            <span className={`text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${i === activeIdx ? "text-[#111827]" : "text-gray-400"}`}>
                                {s.label}
                            </span>
                            {i < ORDER_STAGES.length - 1 && <div className={`w-3 h-1.5 rounded-full ${i < activeIdx ? "bg-[#111827]" : "bg-gray-200"}`} />}
                        </div>
                    );
                })}
            </div>

            {/* Controls */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Status</span>
                    <select value={status} onChange={(e) => setStatus(e.target.value)}
                        className="mt-1 w-full h-9 px-2 rounded-sm border border-gray-300 text-sm bg-white" data-testid="order-tracking-status">
                        {ORDER_STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Voraussichtl. Liefertermin (ETA)</span>
                    <input type="date" value={eta} onChange={(e) => setEta(e.target.value)}
                        className="mt-1 w-full h-9 px-2 rounded-sm border border-gray-300 text-sm" data-testid="order-tracking-eta" />
                </label>
            </div>

            <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Notiz (intern / im Verlauf)</span>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="z. B. Verladung in Pontedera bestätigt"
                    className="mt-1 w-full h-9 px-2 rounded-sm border border-gray-300 text-sm" data-testid="order-tracking-note" />
            </label>

            <div className="border-t border-gray-100 pt-3 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="w-4 h-4 accent-[#111827]" data-testid="order-tracking-notify" />
                    <Bell className="w-4 h-4 text-[#FFCC00]" /> Kunde über diese Änderung benachrichtigen <span className="text-[10px] text-gray-400">(Versand gemockt)</span>
                </label>
                {notify && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-6">
                        <input value={custName} onChange={(e) => setCustName(e.target.value)} placeholder="Kundenname"
                            className="h-9 px-2 rounded-sm border border-gray-300 text-sm" data-testid="order-tracking-cust-name" />
                        <input value={custEmail} onChange={(e) => setCustEmail(e.target.value)} placeholder="Kunden-E-Mail" type="email"
                            className="h-9 px-2 rounded-sm border border-gray-300 text-sm" data-testid="order-tracking-cust-email" />
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
                {trackUrl ? (
                    <button type="button" onClick={copyLink} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#111827] hover:underline" data-testid="order-tracking-copy-link">
                        {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                        {copied ? "Link kopiert" : "Kunden-Tracking-Link kopieren"}
                    </button>
                ) : <span className="text-[11px] text-gray-400">Kunden-Link wird nach dem ersten Speichern erzeugt.</span>}
                <button type="button" onClick={save} disabled={saving}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00] text-sm font-bold disabled:opacity-60"
                    data-testid="order-tracking-save">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />} Speichern
                </button>
            </div>
        </div>
    );
}
